import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, X, Mail } from 'lucide-react';

import { UserAvatar } from '@/components/shared/UserAvatar';
import { RoleBadge } from '@/components/shared/RoleBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { teamService, type TeamMember } from '@/services/team.service';
import { groupService, type WorkspaceUser } from '@/services/group.service';

interface TeamMembersDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  teamId: string;
  teamName: string;
  canManage: boolean;
  canChangeRoles: boolean;
}

export function TeamMembersDialog({
  open, onOpenChange, teamId, teamName, canManage, canChangeRoles,
}: TeamMembersDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [memberSearch, setMemberSearch] = useState('');
  const [inviteInput, setInviteInput] = useState('');
  const [suggestions, setSuggestions] = useState<WorkspaceUser[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const wsId = localStorage.getItem('active_workspace_id') || '';

  const { data, isLoading } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => teamService.listMembers(teamId).then((r) => r.data),
    enabled: open,
  });

  const members: TeamMember[] = data?.data ?? [];
  const filtered = memberSearch
    ? members.filter((m) => m.user?.full_name?.toLowerCase().includes(memberSearch.toLowerCase()))
    : members;

  async function handleInviteInputChange(val: string) {
    setInviteInput(val);
    if (val.length >= 3 && wsId) {
      try {
        const res = await groupService.searchWorkspaceMembers(wsId, val);
        const existingIds = new Set(members.map((m) => m.user?.id));
        const wsMembers = (res.data.data || [])
          .map((m) => m.user)
          .filter((u) => u && !existingIds.has(u.id));
        setSuggestions(wsMembers as WorkspaceUser[]);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
    queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    queryClient.invalidateQueries({ queryKey: ['teams'] });
  }

  const addMut = useMutation({
    mutationFn: (userId: string) => teamService.addMember(teamId, { user_id: userId, role: 'member' }),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Member added' });
      setInviteInput(''); setSuggestions([]); setShowSuggestions(false);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to add member', variant: 'destructive' }),
  });

  const removeMut = useMutation({
    mutationFn: (memberId: string) => teamService.removeMember(teamId, memberId),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Member removed' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const updateRoleMut = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) => teamService.updateMemberRole(teamId, memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      toast({ title: 'Role updated' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Members - {teamName}</DialogTitle>
          <DialogDescription>
            {canManage ? 'Add or remove members and manage their roles' : 'View team members'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {canManage && (
            <div className="border-2 border-primary/20 rounded-lg p-4 space-y-3 bg-primary/5">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <h4 className="font-medium">Add New Member</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Search by name (min 3 chars) to add existing workspace users.
              </p>
              <div className="relative">
                <Input
                  placeholder="Search workspace members..."
                  value={inviteInput}
                  onChange={(e) => handleInviteInputChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                  className="flex-1"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md">
                    {suggestions.map((u) => (
                      <button
                        key={u.id}
                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-accent transition-colors"
                        onMouseDown={(e) => { e.preventDefault(); addMut.mutate(u.id); }}
                      >
                        <UserAvatar name={u.full_name} avatarUrl={u.avatar_url} size="xs" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{u.full_name}</p>
                          <p className="text-xs text-muted-foreground">Click to add to team</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <h4 className="font-medium text-sm">Current Members ({members.length})</h4>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Filter members..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} className="pl-8" />
            </div>
            {isLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-14" />)}</div>
            ) : (
              filtered.map((member) => {
                const isOwner = member.role === 'owner';
                return (
                  <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={member.user?.full_name ?? 'Unknown'} avatarUrl={member.user?.avatar_url} size="sm" />
                      <div>
                        <p className="font-medium">{member.user?.full_name}</p>
                        <p className="text-sm text-muted-foreground">{member.user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canChangeRoles && !isOwner ? (
                        <Select
                          value={member.role}
                          onValueChange={(v) => updateRoleMut.mutate({ memberId: member.id, role: v })}
                        >
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="leader">Leader</SelectItem>
                            <SelectItem value="owner">Owner</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <RoleBadge role={member.role} />
                      )}
                      {canManage && !isOwner && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeMut.mutate(member.id)}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
